<template>
	<div class="step-section-card">
		<div class="step-section-text">
			<div class="d-flex flex-row justify-space-between align-start">
				<div
					:class="{ 'mt-3': showYesNoButtons }"
				>
					<h3 class="step-section-title">
						{{ title }}
					</h3>
					<p
						v-if="description"
						class="step-section-description mr-5"
					>
						{{ description }}
					</p>
				</div>
				<div
					v-if="showYesNoButtons"
					class="d-flex flex-row gap-2 yes-no-container"
				>
						<v-btn
							small
							:color="value === true ? 'primary' : 'default'"
							:outlined="value !== true"
							:disabled="disabled"
							class="yes-no-btn mr-2 text-none"
							@click="handleYesClick"
						>
							{{ yesButtonText }}
						</v-btn>
						<v-btn
							small
							:color="value === false ? 'primary' : 'default'"
							:outlined="value !== false"
							:disabled="disabled"
							class="yes-no-btn text-none"
							@click="handleNoClick"
						>
							{{ noButtonText }}
						</v-btn>
				</div>
			</div>
		</div>
		<div class="step-section-content">
			<slot />
		</div>
	</div>
</template>

<script lang="ts">
import Vue from 'vue'

export interface QuestionCardProps {
	/** The main question title displayed in blue */
	title: string
	/** Optional descriptive text shown below the title */
	description?: string
	/** Whether to show yes/no buttons */
	showYesNoButtons?: boolean
	/** The current value for yes/no buttons (true = yes, false = no, null = no selection) */
	value?: boolean | null
	/** Whether the buttons are disabled */
	disabled?: boolean
	/** Text for the 'yes' button */
	yesButtonText?: string
	/** Text for the 'no' button */
	noButtonText?: string
}

export default Vue.extend({
	name: 'QuestionCard',
	props: {
		title: {
			type: String,
			required: true
		},
		description: {
			type: String,
			default: null
		},
		showYesNoButtons: {
			type: Boolean,
			default: false
		},
		value: {
			type: [Boolean, null],
			default: null
		},
		disabled: {
			type: Boolean,
			default: false
		},
		yesButtonText: {
			type: String,
			default: 'Yes'
		},
		noButtonText: {
			type: String,
			default: 'No'
		}
	},
	methods: {
		handleYesClick() {
			this.$emit('input', true)
			this.$emit('yes-clicked', true)
		},
		handleNoClick() {
			this.$emit('input', false)
			this.$emit('no-clicked', false)
		}
	}
})
</script>

<style lang="scss" scoped>
/* Step Section Card Styling - StepperAccordion pattern */
.step-section-card {
	background: transparent;
	padding: 16px 0;
	margin-bottom: 0;
	box-shadow: none;
	
	// &:not(:first-child) {
	// 	padding-top: 24px;
	// }
}

.step-section-text {
	flex: 1;
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.step-section-title {
	margin: 0;
	font-weight: 500;
	font-size: 14px;
	line-height: 1.4;
}

.step-section-description {
	margin: 0;
	font-size: 12px;
	line-height: 1.5;
}

.step-section-content {
	margin-top: 12px;
	max-width: 1000px;
}

.yes-no-btn {
	min-height: 38px;
	position: relative;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	border-radius: 0.5rem;
	border: 1px solid #dfe3e7;
	padding: 0.5rem 1rem;
	font-size: 0.825rem;
	font-weight: 400;
	transition: all 0.2s ease-in-out;
	cursor: pointer;
	min-width: 60px;
	
	&:focus {
		outline: none;
	}
	
	&:focus-visible {
		box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5);
		border-radius: 0.5rem;
	}
	
	&:disabled {
		cursor: not-allowed;
		opacity: 0.5;
	}
}

</style>